import {
    buildFuelPlansFromStoredRows,
    FUEL_ROW_PLAN_RANGE_QUERY,
    type FuelOptimizerLegPlan,
    type FuelStoredRangeQueryResult,
    type FuelStoredReadRow,
} from '@eflight/shared/dist/fuelChain';
import { API, graphqlOperation } from 'aws-amplify';

/**
 * (#3029) Načtení osy letadla pro PILOTNÍ data — jediný síťový krok celé pilotní palivové cesty.
 *
 * Volá ho SYNCHRONIZACE doplňkových dat (`Datasources/FuelPlans`), která z výsledku uloží
 * kompaktní projekci do lokální databáze. Obrazovky samotné síť nepoužívají: čtou uloženou kopii,
 * takže je ztráta signálu nemůže připravit o čísla.
 *
 * Čte se okno kolem nadcházejících legů letadla a k němu stáří osy (`computedAt`).
 */

const MAX_PAGES = 40;

type StateQuery = {
    getAircraftFuelState: { id: string; computedAt: string | null; dirtySeq: number | null; completedSeq: number | null } | null; // prettier-ignore
};

const STATE_QUERY = `
    query PilotFuelAxisState($aircraftId: ID!) {
        getAircraftFuelState(id: $aircraftId) {
            id
            computedAt
            dirtySeq
            completedSeq
        }
    }
`;

export type PilotStoredFuel = {
    plans: Map<string, FuelOptimizerLegPlan>;
    /** Kdy backend osu naposledy dopočítal (ISO) — stáří čísel patří i pilotovi. */
    computedAt: string | null;
    loading: boolean;
    failed: boolean;
};

export async function fetchPilotStoredFuel(params: {
    aircraftId: string;
    fromIso: string;
    toIso: string;
}): Promise<PilotStoredFuel> {
    const { aircraftId, fromIso, toIso } = params;
    const rows: FuelStoredReadRow[] = [];
    let nextToken: string | null = null;
    for (let page = 0; page < MAX_PAGES; page += 1) {
        const response = (await API.graphql(
            graphqlOperation(FUEL_ROW_PLAN_RANGE_QUERY, { aircraftId, from: fromIso, to: toIso, nextToken })
        )) as { data: FuelStoredRangeQueryResult };
        const connection = response.data.listAircraftFuelRowsByAircraftId;
        for (const item of connection?.items ?? []) {
            if (item != null) {
                rows.push(item);
            }
        }
        nextToken = connection?.nextToken ?? null;
        if (nextToken == null) {
            break;
        }
    }

    const state = (await API.graphql(graphqlOperation(STATE_QUERY, { aircraftId }))) as { data: StateQuery };

    return {
        plans: buildFuelPlansFromStoredRows(rows),
        computedAt: state.data.getAircraftFuelState?.computedAt ?? null,
        loading: false,
        failed: false,
    };
}
